import {
  displayCentsInDollars,
  formatDateTimeShort,
  renderProviderImage,
  renderProviderName,
} from '../../helpers/helpers';
import { unpaidReportOrdersTitle } from '../../helpers/unpaidReportOrders';
import { UnpaidReportOrder } from '../../types/unpaidReportOrders.types';
// Direct file imports on purpose - the styles barrel triggers a circular init.
import { Box, Div, Img, InfoComponentMobile } from '../../styles/styles';
import { Disclaimer, H5, P } from '../../styles/text';
import { Button } from '../../components/button/button';
import { ModalPortal } from '../../components/modal/modalPortal';
import { TooltipWrapper } from '../Tooltips/Tooltips';
import { ProviderLogoBox, SolidFooter } from './UnpaidReportOrdersModal.styled';
import { PendingUnpaidReportOrders } from './useUnpaidReportOrders';
// Through the barrel and read only on click: routes/adminConstants imports the
// admin pages, so a direct import from here would close an import cycle.
import { ADMIN_ORDER_DETAILS } from '@vgrubs/components';

/** A null amount is unknown, not zero. */
const centsOrDash = (cents: number | null) =>
  cents === null ? '-' : displayCentsInDollars(cents);

type Props = {
  pending: PendingUnpaidReportOrders | null;
  closeModal: () => void;
};

/**
 * Asks an admin to confirm paying out, from the order subtotal, the orders a
 * disconnect or delete would leave without their provider payment report.
 * Cancel leaves everything as it was - nothing was disconnected or deleted.
 */
export const UnpaidReportOrdersModal = ({ pending, closeModal }: Props) => {
  if (!pending) return null;
  const { orders, confirm } = pending;
  // A location delete can strand orders of several brands; only then is the
  // brand worth a mention on each row.
  const severalBrands = new Set(orders.orders.map((o) => o.brandId)).size > 1;

  return (
    <ModalPortal
      isOpen
      closeModal={closeModal}
      logo={false}
      header={unpaidReportOrdersTitle(orders.count)}
      zIndex={200}
      // The whole body scrolls under a footer that keeps the total and the
      // decision in view; the total plus one row of buttons is about as tall
      // as two stacked buttons.
      footer={
        <SolidFooter>
          <Div $flex $column $gap="8px" $mb="16">
            <Div $flex $alignCenter $between>
              <H5 $m="0">Order Subtotal</H5>
              <H5 $m="0">{displayCentsInDollars(orders.subTotal)}</H5>
            </Div>
            {orders.clientGets !== undefined && (
              <Div $flex $alignCenter $between>
                <H5 $m="0">Client gets</H5>
                <H5 $m="0">{displayCentsInDollars(orders.clientGets)}</H5>
              </Div>
            )}
          </Div>
          <Div $flex $gap="16px">
            <Button
              $w="100%"
              $selected
              onClick={() => {
                closeModal();
                confirm();
              }}
            >
              Confirm
            </Button>
            <Button $w="100%" $gray $selected onClick={closeModal}>
              Cancel
            </Button>
          </Div>
        </SolidFooter>
      }
      $numOfFloatingButtons={2}
    >
      <P $mb="24">{orders.message}</P>

      <Div $flex $column $gap="8px">
        {orders.orders.map((order) => (
          <OrderCard
            key={order.orderId}
            order={order}
            showBrand={severalBrands}
          />
        ))}
      </Div>
    </ModalPortal>
  );
};

/**
 * One order, laid out like a card of the mobile order list: the ids copy on
 * click, the provider logo sits in the top right corner. The card opens the
 * order in a new tab, so the dialog - and the disconnect or delete waiting on
 * it - stays where it is.
 */
const OrderCard = ({
  order,
  showBrand,
}: {
  order: UnpaidReportOrder;
  showBrand: boolean;
}) => (
  <Box
    $flex
    $alignStart
    $gap="16px"
    $p="16px 20px"
    $pSM="16px 20px"
    $pointer
    style={{ boxShadow: 'none' }}
    onClick={() =>
      window.open(
        ADMIN_ORDER_DETAILS.replace(':id', order.orderId.toString()),
        '_blank',
        'noopener,noreferrer'
      )
    }
  >
    <Div $flex $column $gap="4px" $width="100%" $minWidth="0">
      <InfoComponentMobile
        title="Order ID"
        component={
          <TooltipWrapper
            tooltip={order.orderId.toString()}
            copy={order.orderId.toString()}
          >
            <Disclaimer $m="0">{order.orderId}</Disclaimer>
          </TooltipWrapper>
        }
      />
      <InfoComponentMobile
        title="Number"
        component={
          <TooltipWrapper tooltip={order.number} copy={order.number}>
            <Disclaimer $m="0">{order.number}</Disclaimer>
          </TooltipWrapper>
        }
      />
      <InfoComponentMobile
        title="Date & Time"
        component={
          <Disclaimer $m="0">{formatDateTimeShort(order.createdAt)}</Disclaimer>
        }
      />
      {showBrand && (
        <InfoComponentMobile
          title="Brand ID"
          component={<Disclaimer $m="0">{order.brandId}</Disclaimer>}
        />
      )}
      <InfoComponentMobile
        title="Order Subtotal"
        component={
          <Disclaimer $m="0">{centsOrDash(order.subTotal)}</Disclaimer>
        }
      />
      {order.clientGets !== undefined && (
        <InfoComponentMobile
          title="Client gets"
          component={
            <Disclaimer $m="0">{centsOrDash(order.clientGets)}</Disclaimer>
          }
        />
      )}
    </Div>

    <ProviderLogoBox>
      <Img
        src={renderProviderImage(order.provider, true)}
        alt={renderProviderName(order.provider)}
      />
    </ProviderLogoBox>
  </Box>
);
